import { useCurrentRegion } from '../stores/region';
import { loadRegionCached, type LoadedRegion } from '../services/runtime';
import type {
  RivalDetailDto,
  RivalDto,
  AdminStatsDto,
  NotificationPage,
  AdminUserPage,
  AuditLogDto,
  EventDetailDto,
  EventListDto,
  EventSummaryDto,
  EventTerritoryDto,
  ClubDetailDto,
  ClubLeaderboardPage,
  ClubSearchPage,
  MyClubDto,
  UserSearchItem,
  GameSettingsDto,
} from '@rw/shared';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type QueryClient,
} from '@tanstack/react-query';
import type {
  GameEventDto,
  LeaderboardPage,
  LeaderboardType,
  PaginatedRuns,
  PlayerBadgesDto,
  PlayerStats,
  RegionDto,
  RunDetailDto,
  TerritoryDetailDto,
  TerritoryStatsDto,
} from '@rw/shared';
import { api } from '../lib/api';
import { setAppBadge } from '../lib/app-badge';
import { env } from '../lib/env';
import { useAuth } from '../stores/auth';

export const qk = {
  notificationsUnread: ['notifications', 'unread'] as const,
  notifications: ['notifications', 'list'] as const,
  regions: ['regions'] as const,
  myStats: ['me', 'stats'] as const,
  rivals: ['me', 'rivals'] as const,
  rival: (id: string) => ['me', 'rivals', id] as const,
  playerStats: (id: string) => ['players', id, 'stats'] as const,
  myBadges: ['me', 'badges'] as const,
  playerBadges: (id: string) => ['players', id, 'badges'] as const,
  leaderboard: (type: LeaderboardType, regionId?: string) => ['leaderboard', type, regionId ?? 'all'] as const,
  runs: ['runs'] as const,
  run: (id: string) => ['runs', id] as const,
  territory: (regionId: string, key: string) => ['territory', regionId, key] as const,
  territoryStats: (regionId: string) => ['territory-stats', regionId] as const,
  owned: (regionId: string) => ['owned', regionId] as const,
  events: ['events'] as const,
};

export function useRegions() {
  return useQuery({ queryKey: qk.regions, queryFn: () => api<RegionDto[]>('/regions'), staleTime: 10 * 60_000 });
}

/** The city being shown: the one chosen or located last, else the default (Đà Nẵng). */
export function useRegion() {
  const q = useRegions();
  const current = useCurrentRegion((s) => s.regionId);
  const region = q.data?.find((r) => r.id === current) ?? q.data?.find((r) => r.id === env.defaultRegionId) ?? q.data?.[0];
  return { ...q, region };
}

/** A specific region's grid and area (e.g. a run's city), loaded once per region. */
export function useLoadedRegion(regionId: string | null | undefined): LoadedRegion | null {
  const q = useRegions();
  const dto = q.data?.find((r) => r.id === regionId);
  return dto ? loadRegionCached(dto) : null;
}

export function useMyStats() {
  return useQuery({ queryKey: qk.myStats, queryFn: () => api<PlayerStats>('/me/stats') });
}

/** Runners you traded sectors with; refreshed with the other competitive data (['me']). */
export function useRivals() {
  return useQuery({ queryKey: qk.rivals, queryFn: () => api<RivalDto[]>('/me/rivals') });
}

export function useRival(id: string, enabled = true) {
  return useQuery({ queryKey: qk.rival(id), queryFn: () => api<RivalDetailDto>(`/me/rivals/${id}`), enabled });
}

/** A runner's badges; for me, the server also catches up on any not awarded yet. */
export function useBadges(id: string | null) {
  return useQuery({
    queryKey: id ? qk.playerBadges(id) : qk.myBadges,
    queryFn: () => api<PlayerBadgesDto>(id ? `/users/${id}/badges` : '/me/badges'),
  });
}

export function usePlayerStats(id: string) {
  return useQuery({ queryKey: qk.playerStats(id), queryFn: () => api<PlayerStats>(`/users/${id}/stats`) });
}

/** A city's board, or the national one (no city). */
export function useLeaderboard(type: LeaderboardType, regionId?: string, pageSize = 25) {
  return useInfiniteQuery({
    queryKey: qk.leaderboard(type, regionId),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => api<LeaderboardPage>(`/leaderboard/${type}`, { query: { page: pageParam, pageSize, ...(regionId ? { regionId } : {}) } }),
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
  });
}

/** Profile shows the latest few; the runs page pages through 20 at a time. */
export const RECENT_RUNS = 5;
export const RUNS_PAGE = 20;

export function useRuns(limit = RUNS_PAGE, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...qk.runs, 'list', limit],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api<PaginatedRuns>('/runs', { query: { cursor: pageParam, limit } }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function usePlayerRuns(playerId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['players', playerId, 'runs'],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api<PaginatedRuns>(`/users/${playerId}/runs`, { query: { cursor: pageParam, limit: RUNS_PAGE } }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useRun(id: string | undefined) {
  return useQuery({ queryKey: qk.run(id ?? ''), queryFn: () => api<RunDetailDto>(`/runs/${id}`), enabled: !!id });
}

export function useTerritory(regionId: string | undefined, key: string | null) {
  return useQuery({
    queryKey: qk.territory(regionId ?? '', key ?? ''),
    queryFn: () => api<TerritoryDetailDto>(`/regions/${regionId}/territories/${key}`),
    enabled: !!regionId && !!key,
    retry: (n, e) => (e as { status?: number }).status !== 404 && n < 2,
  });
}

export function useTerritoryStats(regionId: string | undefined) {
  return useQuery({
    queryKey: qk.territoryStats(regionId ?? ''),
    queryFn: () => api<TerritoryStatsDto>(`/regions/${regionId}/stats`),
    enabled: !!regionId,
  });
}

export function useEvents(limit = 15) {
  return useQuery({
    queryKey: qk.events,
    queryFn: () => api<GameEventDto[]>('/events', { query: { limit } }),
    placeholderData: keepPreviousData,
  });
}

/** Everything that depends on competitive state after a run or a realtime event. */
export function invalidateCompetitive(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['me'] });
  void qc.invalidateQueries({ queryKey: ['leaderboard'] });
  void qc.invalidateQueries({ queryKey: ['territory-stats'] });
  void qc.invalidateQueries({ queryKey: ['owned'] });
  void qc.invalidateQueries({ queryKey: ['territory'] });
  void qc.invalidateQueries({ queryKey: qk.events });
  void qc.invalidateQueries({ queryKey: ['players'] });
  void qc.invalidateQueries({ queryKey: ['event'] });
  void qc.invalidateQueries({ queryKey: ['event-map'] });
}

// ───────────── clubs ─────────────

export const clubKeys = {
  mine: ['club', 'mine'] as const,
  detail: (id: string) => ['club', 'detail', id] as const,
  search: (q: string) => ['club', 'search', q] as const,
  leaderboard: (type: LeaderboardType, regionId?: string) => ['club', 'leaderboard', type, regionId ?? 'all'] as const,
  users: (q: string) => ['club', 'users', q] as const,
};

export function useMyClub() {
  return useQuery({ queryKey: clubKeys.mine, queryFn: () => api<MyClubDto>('/me/club') });
}

export function useClub(id: string | undefined) {
  return useQuery({ queryKey: clubKeys.detail(id ?? ''), queryFn: () => api<ClubDetailDto>(`/clubs/${id}`), enabled: !!id });
}

export function useClubSearch(q: string) {
  return useQuery({
    queryKey: clubKeys.search(q),
    queryFn: () => api<ClubSearchPage>('/clubs', { query: { q, pageSize: 30 } }),
    placeholderData: keepPreviousData,
  });
}

export function useClubLeaderboard(type: LeaderboardType, regionId?: string) {
  return useQuery({
    queryKey: clubKeys.leaderboard(type, regionId),
    queryFn: () => api<ClubLeaderboardPage>(`/clubs/leaderboard/${type}`, { query: regionId ? { regionId } : {} }),
  });
}

export function useUserSearch(q: string) {
  return useQuery({
    queryKey: clubKeys.users(q),
    queryFn: () => api<UserSearchItem[]>('/users/search', { query: { q } }),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
}

export function invalidateClubs(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['club'] });
  void qc.invalidateQueries({ queryKey: ['owned'] });
  // Club name and logo also show on player profiles.
  void qc.invalidateQueries({ queryKey: ['players'] });
  void qc.invalidateQueries({ queryKey: qk.myStats });
}

// ───────────── events (competitions) & moderation ─────────────
export const eventKeys = {
  list: ['event-list'] as const,
  detail: (id: string) => ['event', id] as const,
  code: (code: string) => ['event-code', code] as const,
  map: (id: string) => ['event-map', id] as const,
};

export function useEventList() {
  return useQuery({ queryKey: eventKeys.list, queryFn: () => api<EventListDto>('/competitions'), refetchInterval: 60_000 });
}

export function useEventDetail(id: string | undefined) {
  return useQuery({
    queryKey: eventKeys.detail(id ?? ''),
    queryFn: () => api<EventDetailDto>(`/competitions/${id}`),
    enabled: !!id,
    refetchInterval: 30_000,
  });
}

export function useEventByCode(code: string | undefined) {
  return useQuery({ queryKey: eventKeys.code(code ?? ''), queryFn: () => api<EventDetailDto>(`/competitions/code/${code}`), enabled: !!code });
}

export function useEventMap(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: eventKeys.map(id ?? ''),
    queryFn: () => api<EventTerritoryDto[]>(`/competitions/${id}/map`),
    enabled: !!id && enabled,
    refetchInterval: 60_000,
  });
}

export function invalidateEvents(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['event-list'] });
  void qc.invalidateQueries({ queryKey: ['event'] });
  void qc.invalidateQueries({ queryKey: ['event-code'] });
  void qc.invalidateQueries({ queryKey: ['event-map'] });
}

export function useModerationEvents() {
  return useQuery({ queryKey: ['admin', 'events'], queryFn: () => api<EventSummaryDto[]>('/admin/events') });
}

export function useAdminStats() {
  return useQuery({ queryKey: ['admin', 'stats'], queryFn: () => api<AdminStatsDto>('/admin/stats'), refetchInterval: 60_000 });
}

/** The bell's badge: unread in-app notifications (pushed live over the WebSocket). */
export function useUnreadNotifications() {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: qk.notificationsUnread,
    queryFn: async () => {
      const r = await api<{ unread: number }>('/notifications/unread');
      setAppBadge(r.unread);
      return r;
    },
    enabled: !!token,
    staleTime: 30_000,
  });
}

export function useNotifications() {
  return useInfiniteQuery({
    queryKey: qk.notifications,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api<NotificationPage>('/notifications', { query: { before: pageParam } }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

const ADMIN_USERS_PAGE = 50;
export function useAdminUsers(q: string, sort: 'joined' | 'seen' | 'xp') {
  return useInfiniteQuery({
    queryKey: ['admin', 'users', q, sort],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<AdminUserPage>('/admin/users', { query: { q: q || undefined, sort, limit: ADMIN_USERS_PAGE, offset: pageParam } }),
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
}

export function useGameSettings() {
  return useQuery({ queryKey: ['admin', 'settings'], queryFn: () => api<GameSettingsDto>('/admin/settings') });
}

export function useAuditLog() {
  return useQuery({ queryKey: ['admin', 'audit'], queryFn: () => api<AuditLogDto[]>('/admin/audit', { query: { limit: 100 } }) });
}
